import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useMemo } from "react";

import { AbilityInfo, assetName, formatPercent, useAssets } from "@/features/matches";

import { formatBuyTime } from "../../lib/formatBuyTime";
import type { CatalogAbilityOrder } from "../../model/HeroBuilds";
import { type Ability, AbilityIcon } from "./AbilityIcon";

const TITLE = msg({ id: "builds.details.order", message: "Skill order" });
const ABILITY = msg({ id: "builds.details.order_ability", message: "Ability" });
const ULTIMATE = msg({ id: "builds.details.order_ultimate", message: "{name} (ultimate)" });
const PRIORITY = msg({ id: "builds.details.order_priority", message: "Max out in turn" });
const CAPTION_TABLE = {
  build: msg({
    id: "builds.details.order_caption_table",
    message: "{share} of players with this build level up like this · the minute is on top",
  }),
  hero: msg({
    id: "builds.overview.order_caption_table",
    message: "{share} of {hero} players level up like this · the minute is on top",
  }),
} as const;
const CAPTION_STRIP = {
  build: msg({
    id: "builds.details.order_caption_strip",
    message: "{share} of players with this build level up like this · the minute is under the icon",
  }),
  hero: msg({
    id: "builds.overview.order_caption_strip",
    message: "{share} of {hero} players level up like this · the minute is under the icon",
  }),
} as const;
const STEPS = {
  build: msg({
    id: "builds.details.order_steps",
    message: "With this build it is leveled up at steps {steps}",
  }),
  hero: msg({
    id: "builds.overview.order_steps",
    message: "{hero} players level it up at steps {steps}",
  }),
} as const;

type AbilityOrderProps = {
  order: CatalogAbilityOrder;
  // Прокачка сильных с этой сборкой или всех сильных на герое (страница героя, «19д»).
  scope?: "build" | "hero";
  hero?: string;
  // Заголовок над сеткой; на странице героя он свой.
  showTitle?: boolean;
};

// Самый частый у сильных порядок прокачки: строка «до конца по очереди», на широком экране —
// сетка «умение × шаг» с номером шага, на узком — лента иконок по шагам. Строки — в порядке,
// в котором умение впервые качают, ульта — последней. Умение по наведению или нажатию
// показывает описание, как в игре.
export const AbilityOrder = ({
  order,
  scope = "build",
  hero = "",
  showTitle = true,
}: AbilityOrderProps) => {
  const { _, i18n } = useLingui();
  const { data } = useAssets();
  const lookup = useMemo(
    () => new Map((data?.abilities ?? []).map((ability) => [ability.id, ability])),
    [data],
  );
  const ability = (id: number): Ability => {
    const asset = lookup.get(id);
    return {
      id,
      name: asset === undefined ? `#${id}` : assetName(asset.name),
      iconUrl: asset?.icon_url ?? null,
      ultimate: asset?.ultimate === true,
    };
  };
  const firstSeen = [...new Set(order.abilities)];
  const isUltimate = (id: number) => lookup.get(id)?.ultimate === true;
  const rows = [
    ...firstSeen.filter((id) => !isUltimate(id)),
    ...firstSeen.filter((id) => isUltimate(id)),
  ].map(ability);
  const label = (row: Ability) =>
    row.ultimate ? _({ ...ULTIMATE, values: { name: row.name } }) : row.name;
  // Докачивают умение на последнем шаге, где оно встречается: по нему — очередь «до конца».
  const lastStep = new Map(order.abilities.map((id, index) => [id, index]));
  const priority = [...lastStep.keys()]
    .sort((a, b) => (lastStep.get(a) ?? 0) - (lastStep.get(b) ?? 0))
    .map(ability);
  const footer = (id: number) =>
    _({
      ...STEPS[scope],
      values: {
        hero,
        steps: order.abilities
          .flatMap((step, index) => (step === id ? [String(index + 1)] : []))
          .join(", "),
      },
    });
  const share = formatPercent(order.share, i18n.locale);
  return (
    <section className="@container flex flex-col gap-2" aria-label={_(TITLE)}>
      {showTitle && <h4 className="text-[15px] font-semibold">{_(TITLE)}</h4>}
      <div className="flex flex-wrap items-center gap-2 text-[13px] text-muted">
        <span>{_(PRIORITY)}</span>
        <ol className="flex flex-wrap items-center gap-1.5">
          {priority.map((row, index) => (
            <li key={row.id}>
              <AbilityInfo abilityId={row.id} name={row.name} footer={footer(row.id)}>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-raised py-0.5 pr-2.5 pl-1 text-foreground">
                  <AbilityIcon ability={row} size={24} />
                  <span className="font-mono font-semibold text-accent">{index + 1}</span>
                  {label(row)}
                </span>
              </AbilityInfo>
            </li>
          ))}
        </ol>
      </div>
      <div className="hidden overflow-x-auto pb-1 @xl:block">
        <table className="border-separate border-spacing-1 text-xs">
          <thead>
            <tr>
              <th scope="col" className="min-w-[190px] text-left font-normal text-subtle">
                {_(ABILITY)}
              </th>
              {order.times_s.map((time, index) => (
                <th
                  // Шаги прокачки не переставляются: номер шага — устойчивый ключ.
                  key={index}
                  scope="col"
                  className="w-[34px] font-mono text-[11px] font-normal text-subtle tabular-nums"
                >
                  {formatBuyTime(time)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <th scope="row" className="text-left font-medium whitespace-nowrap">
                  <AbilityInfo abilityId={row.id} name={label(row)} footer={footer(row.id)}>
                    <span className="inline-flex items-center gap-2.5">
                      <AbilityIcon ability={row} size={34} />
                      {label(row)}
                    </span>
                  </AbilityInfo>
                </th>
                {order.abilities.map((id, index) =>
                  id === row.id ? (
                    <td
                      key={index}
                      className="h-[30px] w-[34px] rounded-md bg-accent text-center font-mono font-semibold text-accent-foreground"
                    >
                      {index + 1}
                    </td>
                  ) : (
                    <td key={index} className="h-[30px] w-[34px] rounded-md bg-surface-raised" />
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ol className="flex w-full gap-1 overflow-x-auto pb-1 @xl:hidden">
        {order.abilities.map((id, index) => {
          const step = ability(id);
          return (
            <li
              key={index}
              title={step.name}
              className="flex w-[34px] shrink-0 flex-col items-center gap-0.5"
            >
              <AbilityInfo abilityId={id} name={step.name} footer={footer(id)}>
                <AbilityIcon ability={step} size={30} />
              </AbilityInfo>
              <span className="font-mono text-[10px] text-foreground-soft">
                {formatBuyTime(order.times_s[index] ?? 0)}
              </span>
            </li>
          );
        })}
      </ol>
      <span className="text-xs text-subtle @xl:hidden">
        {_({ ...CAPTION_STRIP[scope], values: { share, hero } })}
      </span>
      <span className="hidden text-xs text-subtle @xl:inline">
        {_({ ...CAPTION_TABLE[scope], values: { share, hero } })}
      </span>
    </section>
  );
};
